import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import Icon from '../../../components/AppIcon';
import ReportStatCard from './ReportStatCard';
import { resourceService } from '../../../services/resourceService';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];

const ResourceUtilizationTab = ({ dateRange }) => {
  const [stats, setStats] = useState({});
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [dateRange]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [utilResult, resourceResult] = await Promise.all([
        resourceService?.getUtilizationStats(),
        resourceService?.getAll()
      ]);
      if (utilResult?.error) throw utilResult?.error;
      if (resourceResult?.error) throw resourceResult?.error;
      setStats(utilResult?.data || {});
      setResources(resourceResult?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load resource data');
    } finally {
      setLoading(false);
    }
  };

  const categoryData = Object.entries(stats?.byCategory || {})?.map(([cat, counts]) => ({
    name: cat?.charAt(0)?.toUpperCase() + cat?.slice(1),
    total: counts?.total || 0,
    inUse: counts?.inUse || 0,
    available: counts?.available || 0
  }));

  const statusData = [
    { name: 'Available', value: stats?.available || 0 },
    { name: 'In Use', value: stats?.inUse || 0 },
    { name: 'Maintenance', value: stats?.maintenance || 0 }
  ]?.filter(d => d?.value > 0);

  const utilizationRate = (stats?.total || 0) > 0
    ? Math.round(((stats?.inUse || 0) / stats?.total) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <Icon name="AlertCircle" size={20} className="inline mr-2" />{error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ReportStatCard title="Total Resources" value={stats?.total || 0} icon="Package" color="accent" subtitle="" trend={0} trendLabel="" />
        <ReportStatCard title="In Use" value={stats?.inUse || 0} icon="Activity" color="blue" subtitle="" trend={0} trendLabel="" />
        <ReportStatCard title="Available" value={stats?.available || 0} icon="CheckCircle2" color="green" subtitle="" trend={0} trendLabel="" />
        <ReportStatCard title="Utilization Rate" value={`${utilizationRate}%`} icon="BarChart2" color="purple" subtitle="Currently deployed" trend={0} trendLabel="" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Resources by Status</h3>
          {statusData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}>
                  {statusData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No data available</div>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-5">
          <h3 className="text-base font-semibold text-foreground mb-4">Resources by Category</h3>
          {categoryData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                <Tooltip />
                <Bar dataKey="inUse" name="In Use" fill="#3B82F6" stackId="a" />
                <Bar dataKey="available" name="Available" fill="#10B981" stackId="a" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-60 text-muted-foreground text-sm">No data available</div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border">
        <div className="p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Resource Details</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Resource</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Category</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-muted-foreground uppercase">Assignments</th>
              </tr>
            </thead>
            <tbody>
              {resources?.slice(0, 15)?.map(r => (
                <tr key={r?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-foreground">{r?.name}</p>
                    <p className="text-xs text-muted-foreground">{r?.identifier}</p>
                  </td>
                  <td className="px-5 py-3 text-sm text-muted-foreground capitalize">{r?.category}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      r?.status === 'Available' ? 'bg-green-100 text-green-700' :
                      r?.status === 'In Use'? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{r?.status}</span>
                  </td>
                  <td className="px-5 py-3 text-sm text-right text-foreground">
                    {r?.assignments?.length || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {resources?.length === 0 && (
            <div className="p-8 text-center text-muted-foreground text-sm">No resources found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResourceUtilizationTab;
